import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, DollarSign, Save, Plus, Trash2, Search, 
  Download, Upload, RefreshCw, Edit2, X, Building, MapPin
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CATEGORIES = {
  kitchen: "Kitchen Remodeling",
  bathroom: "Bathroom Remodeling", 
  roofing: "Roofing",
  pool: "Pool Construction",
  general: "General Construction",
  flooring: "Flooring (Laminate)",
  painting: "Painting",
  plumbing: "Plumbing",
  water_damage: "Water Damage / Mitigation",
  mold: "Mold Remediation",
  hvac: "HVAC",
  windows: "Window Replacement",
  electrical: "Electrical",
  drywall: "Drywall",
  fence: "Fence Installation",
  concrete: "Concrete (Driveway/Patio)",
  stucco: "Stucco Repair",
  exterior: "Exterior (Soffit/Fascia/Gutter)",
  tile: "Tile Installation",
  interior: "Interior (Popcorn Ceiling)"
};

const TIERS = {
  builder_grade: "Builder Grade / Economy",
  standard: "Standard",
  high_end: "High-End / Luxury"
};

const UNITS = {
  sq_ft: "Sq Ft",
  linear_ft: "Linear Ft",
  each: "Each",
  cubic_ft: "Cubic Ft",
  cubic_yd: "Cubic Yd",
  per_hour: "Per Hour",
  lump_sum: "Lump Sum"
};

const REGIONS = {
  collier: { name: "Collier County", cities: "Naples, Marco Island" },
  broward: { name: "Broward County", cities: "Fort Lauderdale, Hollywood" },
  palm_beach: { name: "Palm Beach County", cities: "West Palm Beach, Boca Raton" }
};

const PricingManager = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [seeding, setSeeding] = useState(false);
  
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    item_name: '',
    description: '',
    unit: 'each',
    tier: 'standard',
    collier_price: 0,
    broward_price: 0,
    palm_beach_price: 0,
    material_cost: 0,
    labor_cost: 0,
    markup_percent: 30
  });

  useEffect(() => {
    fetchPricingItems();
  }, [filterCategory, filterTier]);

  const fetchPricingItems = async () => {
    try {
      let url = `${BACKEND_URL}/api/pricing/items`;
      const params = [];
      if (filterCategory !== 'all') params.push(`category=${filterCategory}`);
      if (filterTier !== 'all') params.push(`tier=${filterTier}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await fetch(url);
      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing/seed`, { method: 'POST' });
      const data = await response.json();
      toast.success(data.message);
      fetchPricingItems();
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const handleSaveItem = async () => {
    if (!formData.category || !formData.item_name) {
      toast.error('Category and Item Name are required');
      return;
    }

    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/pricing/items/${editingItem.id}`
        : `${BACKEND_URL}/api/pricing/items`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? 'Item updated' : 'Item created');
        setShowAddModal(false);
        setEditingItem(null);
        resetForm();
        fetchPricingItems();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this pricing item?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing/items/${itemId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Item deleted');
        fetchPricingItems();
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      subcategory: item.subcategory || '',
      item_name: item.item_name,
      description: item.description || '',
      unit: item.unit,
      tier: item.tier,
      collier_price: item.collier_price || 0,
      broward_price: item.broward_price || 0,
      palm_beach_price: item.palm_beach_price || 0,
      material_cost: item.material_cost || 0,
      labor_cost: item.labor_cost || 0,
      markup_percent: item.markup_percent || 30
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      subcategory: '',
      item_name: '',
      description: '',
      unit: 'each',
      tier: 'standard',
      collier_price: 0,
      broward_price: 0,
      palm_beach_price: 0,
      material_cost: 0,
      labor_cost: 0,
      markup_percent: 30
    });
  };

  const handleExportCSV = () => {
    const headers = ['Category', 'Subcategory', 'Item Name', 'Description', 'Unit', 'Tier', 'Collier Price', 'Broward Price', 'Palm Beach Price'];
    const rows = items.map(item => [
      item.category,
      item.subcategory,
      item.item_name,
      item.description,
      item.unit,
      item.tier,
      item.collier_price,
      item.broward_price,
      item.palm_beach_price
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricing_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Pricing exported to CSV');
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.item_name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.subcategory?.toLowerCase().includes(query)
    );
  });

  // Group items by category and subcategory
  const groupedItems = filteredItems.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = {};
    const sub = item.subcategory || 'general';
    if (!acc[cat][sub]) acc[cat][sub] = [];
    acc[cat][sub].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Volver' : 'Back'}
              </Button>
            </Link>
            <CompanyLogo size="sm" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {language === 'es' ? 'Administrador de Precios' : 'Pricing Manager'}
              </h1>
              <p className="text-sm text-gray-500">
                {language === 'es' ? 'Precios regionales de Florida' : 'Florida Regional Pricing'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Region Info Cards */}
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(REGIONS).map(([key, region]) => (
            <Card key={key} className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-blue-900">{region.name}</h3>
                    <p className="text-xs text-blue-600">{region.cities}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters and Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={language === 'es' ? 'Buscar items...' : 'Search items...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'es' ? 'Todas las Categorías' : 'All Categories'}</SelectItem>
                  {Object.entries(CATEGORIES).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'es' ? 'Todos los Niveles' : 'All Tiers'}</SelectItem>
                  {Object.entries(TIERS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Button 
                  onClick={() => { resetForm(); setEditingItem(null); setShowAddModal(true); }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Agregar Item' : 'Add Item'}
                </Button>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleSeedDatabase}
                  disabled={seeding}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${seeding ? 'animate-spin' : ''}`} />
                  {language === 'es' ? 'Restaurar Precios' : 'Reset Prices'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{filteredItems.length} {language === 'es' ? 'items encontrados' : 'items found'}</span>
          <span className="text-xs text-gray-400">
            {language === 'es' ? 'Precios en USD - Collier / Broward / Palm Beach' : 'Prices in USD - Collier / Broward / Palm Beach'}
          </span>
        </div>

        {/* Pricing Table by Category */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-2 text-gray-500">Loading pricing data...</p>
          </div>
        ) : Object.keys(groupedItems).length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <DollarSign className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No pricing items found</h3>
              <p className="text-gray-500 mt-2">Click "Reset Prices" to load default Florida pricing</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedItems).map(([category, subcategories]) => (
            <Card key={category} className="overflow-hidden">
              <CardHeader className="bg-gray-100 py-3">
                <CardTitle className="text-lg flex items-center">
                  <Building className="h-5 w-5 mr-2 text-gray-600" />
                  {CATEGORIES[category] || category}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {Object.entries(subcategories).map(([subcategory, subItems]) => (
                  <div key={subcategory} className="border-b last:border-b-0">
                    <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-600 capitalize">
                      {subcategory.replace(/_/g, ' ')}
                    </div>
                    <table className="w-full">
                      <thead className="text-xs text-gray-500 bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Item</th>
                          <th className="px-4 py-2 text-left">Tier</th>
                          <th className="px-4 py-2 text-left">Unit</th>
                          <th className="px-4 py-2 text-right">Collier</th>
                          <th className="px-4 py-2 text-right">Broward</th>
                          <th className="px-4 py-2 text-right">Palm Beach</th>
                          <th className="px-4 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subItems.map((item) => (
                          <tr key={item.id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{item.item_name}</div>
                              {item.description && (
                                <div className="text-xs text-gray-500 truncate max-w-[250px]">{item.description}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={
                                item.tier === 'builder_grade' ? 'secondary' : 
                                item.tier === 'high_end' ? 'default' : 'outline'
                              } className={
                                item.tier === 'high_end' ? 'bg-amber-100 text-amber-800' : ''
                              }>
                                {TIERS[item.tier] || item.tier}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {UNITS[item.unit] || item.unit}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-sm">
                              ${item.collier_price?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-sm">
                              ${item.broward_price?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-sm">
                              ${item.palm_beach_price?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditItem(item)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteItem(item.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  {editingItem 
                    ? (language === 'es' ? 'Editar Item de Precio' : 'Edit Pricing Item')
                    : (language === 'es' ? 'Agregar Item de Precio' : 'Add Pricing Item')
                  }
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddModal(false); setEditingItem(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'es' ? 'Categoría' : 'Category'} *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORIES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{language === 'es' ? 'Subcategoría' : 'Subcategory'}</Label>
                    <Input 
                      value={formData.subcategory}
                      onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                      placeholder="e.g., cabinets, countertops"
                    />
                  </div>
                </div>

                <div>
                  <Label>{language === 'es' ? 'Nombre del Item' : 'Item Name'} *</Label>
                  <Input 
                    value={formData.item_name}
                    onChange={(e) => setFormData({...formData, item_name: e.target.value})}
                    placeholder="e.g., Lower Base Cabinet"
                  />
                </div>

                <div>
                  <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
                  <Input 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Standard 36 inch base cabinet with drawer"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'es' ? 'Unidad' : 'Unit'}</Label>
                    <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(UNITS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{language === 'es' ? 'Nivel de Calidad' : 'Quality Tier'}</Label>
                    <Select value={formData.tier} onValueChange={(v) => setFormData({...formData, tier: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIERS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-lg font-semibold mb-3 block">
                    {language === 'es' ? 'Precios Regionales' : 'Regional Pricing'}
                  </Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-blue-600">Collier County (Naples)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.collier_price}
                          onChange={(e) => setFormData({...formData, collier_price: parseFloat(e.target.value) || 0})}
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-blue-600">Broward County (Ft. Lauderdale)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.broward_price}
                          onChange={(e) => setFormData({...formData, broward_price: parseFloat(e.target.value) || 0})}
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-blue-600">Palm Beach County (W. Palm Beach)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.palm_beach_price}
                          onChange={(e) => setFormData({...formData, palm_beach_price: parseFloat(e.target.value) || 0})}
                          className="pl-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingItem(null); }}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button onClick={handleSaveItem} className="bg-blue-600 hover:bg-blue-700">
                    <Save className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Guardar' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default PricingManager;
